import _, { every, uniqBy } from "lodash";
import moment from "moment";

export function getErrors(config, provider) {
  return (post) => {
    let errors = {};

    const validationSchema = {
      publishTo: getPublishToErrors(config, post, provider),
      text: getTextErrors(config, post, provider),
      attachments: getAttachmentErrors(config, post, provider),
      immediateReply: getImmediateReplyErrors(config, post, provider),
      googlePost: getGooglePostErrors(config, post, provider),
      scheduledTime: getPublishingTimeframe(config, post, provider),
    };

    Object.keys(validationSchema).forEach((key) => {
      const tmpErrors = validationSchema[key](post[key]);
      if (!!tmpErrors && tmpErrors.length) {
        errors[key] = tmpErrors;
      }
    });
    return errors;
  };
}

function getPublishingTimeframe(config, post, provider) {
  return (scheduledTime) => {
    if (
      config.scheduledTime && config.scheduledTime.validDateTime &&
      !config.scheduledTime.validDateTime(scheduledTime)
    ) {
      return [
        "The scheduled time for the post to be posted is not valid. Please try between 8am PT and 5pm PT, or for after the following day at 8am PT.",
      ];
    } else {
      return [];
    }
  };
}

function getPublishToErrors(config, post, provider) {
  return (publishTo) => {
    if (!publishTo || publishTo.length === 0) {
      return ["Select a page to publish to."];
    }
    return [];
  };
}

function getImmediateReplyErrors(config, post, provider) {
  return (immediateReply) => {
    const errors = [];
    const replyLength = (immediateReply || "").trim().length;

    if (
      config.canPublishImmediateReply &&
      config.maxReplyLength &&
      replyLength > config.maxReplyLength
    ) {
      errors.push(
        `Exceeded max ${config.replyWord} length of ${config.maxReplyLength} characters.`
      );
    }
    if (config.requireImmediateReply && replyLength < config.minReplyLength) {
      errors.push(
        `Include an answer with ${config.minReplyLength} or more characters.`
      );
    }

    return errors;
  };
}

function validateURLField(urlField, friendlyLabel, config) {
  const url = urlField || "";
  const isValidUrl = is_url(url);
  if (config.requireValidUrl && !isValidUrl) {
    return `Please add a valid link for ${friendlyLabel}.`;
  }
  return null;
}

function getGooglePostErrors(config, post, provider) {
  return (googlePost) => {
    const errors = [];
    if (!googlePost) return errors;
    if (provider !== "Google") return errors;

    const summaryLength = (googlePost.summary || "").trim().length;
    if (config.minSummaryLength && summaryLength < config.minSummaryLength) {
      errors.push(
        `Add at least ${config.minSummaryLength} character(s) to your summary.`
      );
    } else if (
      config.maxSummaryLength &&
      summaryLength > config.maxSummaryLength
    ) {
      errors.push(
        `Summary exceeded max length of ${config.maxSummaryLength} characters.`
      );
    }

    const eventTitleLength = (googlePost.eventTitle || "").trim().length;
    if (
      config.minEventTitleLength &&
      eventTitleLength < config.minEventTitleLength
    ) {
      errors.push(
        `Enter an event title greater than ${config.minEventTitleLength} characters.`
      );
    } else if (
      config.maxEventTitleLength &&
      eventTitleLength > config.maxEventTitleLength
    ) {
      errors.push(
        `Enter an event title shorter than ${config.maxEventTitleLength} characters.`
      );
    }

    const offerCouponLength = (googlePost.offerCouponCode || "").trim().length;
    if (config.minCouponLength && offerCouponLength < config.minCouponLength) {
      errors.push(
        `Enter a coupon code greater than ${config.minCouponLength} characters.`
      );
    } else if (
      config.maxCouponLength &&
      offerCouponLength > config.maxCouponLength
    ) {
      errors.push(
        `Enter a coupon code shorter than ${config.maxCouponLength} characters.`
      );
    }

    if (
      config.requireValidDateRanges &&
      moment(googlePost.eventStart).isSameOrAfter(moment(googlePost.eventEnd))
    ) {
      errors.push("The start date must be before your end date.");
    }

    // Call callToActionTypes don't require a URL since it's a phone number.
    const callToActionURLError =
      googlePost.callToActionType && googlePost.callToActionType !== "Call"
        ? validateURLField(
            googlePost.callToActionURL,
            "your button",
            config,
            post
          )
        : null;
    if (callToActionURLError) errors.push(callToActionURLError);

    const offerRedeemOnlineUrlError = googlePost.offerRedeemOnlineUrl
      ? validateURLField(
          googlePost.offerRedeemOnlineUrl,
          "the offer redeem link",
          config
        )
      : null;
    if (offerRedeemOnlineUrlError) errors.push(offerRedeemOnlineUrlError);

    const offerTermsConditionsUrlError = googlePost.offerTermsConditions
      ? validateURLField(
          googlePost.offerTermsConditions,
          "the terms and conditions link",
          config
        )
      : null;
    if (offerTermsConditionsUrlError) errors.push(offerTermsConditionsUrlError);

    return errors;
  };
}

// https://stackoverflow.com/questions/161738/what-is-the-best-regular-expression-to-check-if-a-string-is-a-valid-url
function is_url(str) {
  const regexp = /^(?:(?:https?|ftp):\/\/)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:\/\S*)?$/;
  return regexp.test(str);
}

function getTextErrors(config, post, provider) {
  return (text) => {
    const errors = [];
    let textLength = (text || "").trim().length;
    const attachments = !!post.attachments && post.attachments.length > 0;

    if (
      provider === "Twitter" &&
      _.find(post.attachments, { attachmentType: "Link" }) !== undefined
    ) {
      textLength += 24; // t.co link wrapping will shorten
    }

    const hashtags = text.split("#").length - 1;
    if (!!config.maxNumberOfHashtags && hashtags > config.maxNumberOfHashtags) {
      errors.push(
        `There are too many hashtags included in your posts. The maximum number of hashtags accepted is ${config.maxNumberOfHashtags}`
      );
    }

    if (
      textLength < config.minLength &&
      config.canAttachImages &&
      !attachments
    ) {
      errors.push(
        `Add some content, either some text (at least ${config.minLength} characters), or an attachment to the post.`
      );
    } else if (
      textLength < config.minLength &&
      (config.requireDescription || !config.canAttachImages)
    ) {
      errors.push(`Add at least ${config.minLength} characters.`);
    } else if (config.maxLength && textLength > config.maxLength) {
      errors.push(`Exceeded max length of ${config.maxLength} characters.`);
    }

    return errors;
  };
}

function getAttachmentErrors(config, post, provider) {
  return (attachments) => {
    const errors = [];
    const hasAttachments = !!attachments && attachments.length > 0;

    if (hasAttachments && attachments.length > config.maxAttachments) {
      // This error message will supersede all other attachment error messages.
      errors.push(`Posts are limited to ${config.maxAttachments} attachments`);
    }
    if (
      hasAttachments &&
      attachments.filter((x) => x.attachmentType === "Link").length >
        config.maxNumberOfLinks
    ) {
      errors.push(`Posts are limited to ${config.maxAttachments} link`);
    }

    if (config.requireAttachment && !hasAttachments) {
      errors.push(
        `You must attach at least one photo or video to to the post.`
      );
    }
    if (
      !config.canHaveMixedMedia &&
      attachments &&
      uniqBy(attachments, (x) => x.attachmentType).length > 1
    ) {
      errors.push(
        "You can't have more than one type of attachment on this post."
      );
    }
    if (
      !config.canAttachLink &&
      attachments.filter((a) => a.attachmentType === "Link").length
    ) {
      errors.push(
        `You can not attach Links to this post as an attachment. If you'd like to have a link in your post, you can add it to the body.`
      );
    }
    if (config.canAttachImages && hasAttachments) {
      const filtered = attachments.filter((a) => a.attachmentType === "Image");
      const len = filtered.length;
      if (config.maxNumberOfImages && len > config.maxNumberOfImages) {
        errors.push(
          `You can not post ${len} photos as it exceeds the limit of ${config.maxNumberOfImages}.`
        );
      }
      if (!!config.aspectRatio && !!config.aspectRatio.dimensionRequirement) {
        const dimensions = config.aspectRatio.dimensionRequirement;
        const allSizedCorrectly = every(
          filtered,
          isWithinDimensions(dimensions)
        );
        if (!allSizedCorrectly) {
          const plural = attachments.length > 1;
          const errorMsg =
            `` +
            `The image${plural ? "s" : ""} you have attached ${
              plural ? "don't" : "doesn't"
            } meet the height and width requirements for ${provider}. ` +
            `Please select ${plural ? "images" : "another image"} that ${
              plural ? "have" : "has"
            } the minimum accepted width ` +
            `of ${dimensions.minWidth || 0}px and height of ${
              dimensions.minHeight || 0
            }px ` +
            `or a maximum width of ${
              dimensions.maxWidth || 0
            }px and height of ${dimensions.maxHeight || 0}px.`;
          errors.push(errorMsg);
        }
      }
      if (!!config.aspectRatio) {
        const aspectRatio = config.aspectRatio;
        const allSizedCorrectly = every(filtered, isWithinAspect(aspectRatio));
        if (!allSizedCorrectly) {
          const plural = attachments.length > 1;
          const errorMsg =
            `` +
            `The image${plural ? "s" : ""} you have attached ${
              plural ? "don't" : "doesn't"
            } meet the aspect ratio requirements for ${provider}. ` +
            `Please select ${plural ? "images" : "another image"} that ${
              plural ? "have" : "has"
            } an aspect ratio between ` +
            ` ${aspectRatio.min.width || 0}:${
              aspectRatio.min.height || 0
            } and ` +
            ` ${aspectRatio.max.width || 0}:${aspectRatio.max.height || 0}.`;
          errors.push(errorMsg);
        }
      }
    }
    if (config.canAttachVideos) {
      const filtered = attachments.filter((a) => a.attachmentType === "Video");
      const len = filtered.length;
      if (config.maxNumberOfVideos && len > config.maxNumberOfVideos) {
        errors.push(
          `You can not post ${len} videos as it exceeds the limit of ${config.maxNumberOfVideos}.`
        );
      }

      const validDuration = every(filtered, isDurationValid(config));
      if (!validDuration) {
        const errorMsg =
          `` +
          `The video you have attached doesn't meet the duration requirements for ${provider}. ` +
          `Please select another video that has the minimum duration of ${
            config.minVideoDuration || 0
          } seconds and ` +
          `a maximum duration of ${config.maxVideoDuration || 0} seconds.`;
        errors.push(errorMsg);
      }

      if (!!config.videoAspectRatio) {
        const aspectRatio = config.videoAspectRatio;
        const allSizedCorrectly = every(filtered, isWithinAspect(aspectRatio));
        if (!allSizedCorrectly) {
          const plural = attachments.length > 1;
          const errorMsg =
            `` +
            `The video${plural ? "s" : ""} you have attached ${
              plural ? "don't" : "doesn't"
            } meet the aspect ratio requirements for ${provider}. ` +
            `Please select ${plural ? "videos" : "another video"} that ${
              plural ? "have" : "has"
            } an aspect ratio between ` +
            ` ${aspectRatio.min.width || 0}:${
              aspectRatio.min.height || 0
            } and ` +
            ` ${aspectRatio.max.width || 0}:${aspectRatio.max.height || 0}.`;
          errors.push(errorMsg);
        }
      }
      if (
        !!config.aspectRatio &&
        !!config.videoAspectRatio.dimensionRequirement
      ) {
        const dimensions = config.videoAspectRatio.dimensionRequirement;
        const allSizedCorrectly = every(
          filtered,
          isWithinDimensions(dimensions)
        );
        if (!allSizedCorrectly) {
          const plural = attachments.length > 1;
          const errorMsg =
            `` +
            `The video${plural ? "s" : ""} you have attached ${
              plural ? "don't" : "doesn't"
            } meet the height and/or width requirements for ${provider}. ` +
            `Please select ${plural ? "images" : "another video"} that ${
              plural ? "have" : "has"
            } the minimum accepted width ` +
            `of ${dimensions.minWidth || 0}px and height of ${
              dimensions.minHeight || 0
            }px ` +
            `or a maximum width of ${
              dimensions.maxWidth || 0
            }px and height of ${dimensions.maxHeight || 0}px.`;
          errors.push(errorMsg);
        }
      }
    }

    return errors;
  };
}

/**
 * Function that returns a validation function that takes an attachment (video or image),
 * and determines if it is within the required bounds
 *
 * @param dimensionRequirement - the required bounds of the attachment
 * @returns {function(attachment): boolean}
 */
function isWithinDimensions(dimensionRequirement) {
  return (attachment) => {
    let width = attachment.width;
    let height = attachment.height;
    if (!(!!width || !!height)) {
      /*
       * If either is undefined, then define them.
       * This is for the case in which a user directly uploads a photo to S3
       */
      let image = new Image();
      image.src = attachment.url;
      width = image.width;
      height = image.height;
    }
    const minWidth = !!dimensionRequirement.minWidth
      ? width >= dimensionRequirement.minWidth
      : true;
    const maxWidth = !!dimensionRequirement.maxWidth
      ? width <= dimensionRequirement.maxWidth
      : true;
    const minHeight = !!dimensionRequirement.minHeight
      ? height >= dimensionRequirement.minHeight
      : true;
    const maxHeight = !!dimensionRequirement.maxHeight
      ? height <= dimensionRequirement.maxHeight
      : true;
    return every([minWidth, maxWidth, minHeight, maxHeight]);
  };
}

/**
 * Function that returns a validation function that takes an attachment (video or image),
 * and determines if it is within the required bounds
 *
 * @param aspectRatio - the required aspect ratio of the attachment
 * @returns {function(attachment): boolean}
 */
function isWithinAspect(aspectRatio) {
  const minAspect = !!aspectRatio.min
    ? aspectRatio.min.width / aspectRatio.min.height
    : 0;
  const maxAspect = !!aspectRatio.max
    ? aspectRatio.max.width / aspectRatio.max.height
    : 0;

  return (attachment) => {
    let width = attachment.width;
    let height = attachment.height;
    const undefinedSize = !(!!width || !!height);
    if (undefinedSize && attachment.attachmentType === "Video") {
      // TODO: Figure out how to get the size of the video.
      return true;
    }
    if (undefinedSize) {
      /*
       * If either is undefined, then define them.
       * This is for the case in which a user directly uploads a photo to S3
       */
      let image = new Image();
      image.src = attachment.url;
      width = image.width;
      height = image.height;
    }
    const currentAspect = width / height;
    return currentAspect >= minAspect && currentAspect <= maxAspect;
  };
}

/**
 * Function that returns a validation function that takes an attachment (video),
 * and determines if it is within the required bounds for the duration
 *
 * @param config - validation config for a provider
 * @returns {function(attachment): boolean}
 */
function isDurationValid(config) {
  return (attachment) => {
    let duration = attachment.duration;
    if (!duration) {
      let audio = new Audio(attachment.url);
      duration = audio.duration;
    }
    // If the length of the video is less than 3 seconds, it will be greater than 0.0, i.e. 0.0000001 - 3
    if (duration) {
      const minDuration = !!config.minVideoDuration
        ? duration >= config.minVideoDuration
        : true;
      const maxDuration = !!config.maxVideoDuration
        ? duration <= config.maxVideoDuration
        : true;
      return minDuration && maxDuration;
    } else {
      // If a video doesn't have audio, we have no way of determining the length, just let them fail
      return true;
    }
  };
}
